import { useState } from "react";
import { Loader2, X, Lock, RefreshCw, DollarSign, ChevronDown, Eye, EyeOff, PlayCircle } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";
import { isProduction } from "../../../config/environment";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";

interface ModalMercadoPagoIntegrationProps {
    id: string;
    setModalOpenMercadoPago: (open: boolean) => void;
    handlePostMercadoPagoIntegration: (tokenPublic: string, tokenPrivate: string, id: string) => void;
    tokenPublic: string;
    setTokenPublic: (token: string) => void;
    tokenPrivate: string;
    setTokenPrivate: (token: string) => void;
    loading: boolean;
    language: Language;
}

const isTestToken = (token: string): boolean => {
    return token.startsWith("TEST-");
};

const isValidMercadoPagoToken = (token: string): boolean => {
    if (!token.trim()) return true;
    return token.startsWith("APP_USR-") || token.startsWith("TEST-");
};

const MERCADO_PAGO_LOGO = INTEGRATIONS_UI_CONFIG.mercadopago.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

const ModalMercadoPagoIntegration = ({ id, setModalOpenMercadoPago, handlePostMercadoPagoIntegration, tokenPublic, setTokenPublic, tokenPrivate, setTokenPrivate, loading, language }: ModalMercadoPagoIntegrationProps) => {

    const [showPassword, setShowPassword] = useState(false);
    const hasTestToken = isProduction && (isTestToken(tokenPublic) || isTestToken(tokenPrivate));
    const publicTokenError = isProduction && isTestToken(tokenPublic);
    const privateTokenError = isProduction && isTestToken(tokenPrivate);
    const invalidPublicFormat = tokenPublic.trim() !== "" && !isValidMercadoPagoToken(tokenPublic);
    const invalidPrivateFormat = tokenPrivate.trim() !== "" && !isValidMercadoPagoToken(tokenPrivate);
    const hasInvalidFormat = invalidPublicFormat || invalidPrivateFormat;

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!tokenPublic.trim() || !tokenPrivate.trim() || hasTestToken || hasInvalidFormat) return;
        handlePostMercadoPagoIntegration(tokenPublic, tokenPrivate, id);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] overflow-hidden flex flex-col md:flex-row relative">
                <button
                    onClick={() => setModalOpenMercadoPago(false)}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
                >
                    <X size={24} />
                </button>

                {/* Left Side - Form */}
                <div className="flex-1 p-6 md:p-8 flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-12 h-12 flex items-center justify-center shrink-0">
                            <img src={MERCADO_PAGO_LOGO} alt="Mercado Pago" className="w-full h-full object-contain" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                            {dictionary["mercadopago-connect-title"]?.[language] || "Conecta tu cuenta Mercado Pago"}
                        </h2>
                    </div>

                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                        {dictionary["mercadopago-connect-description"]?.[language] || "Al conectar tu cuenta, podrás procesar pagos de manera segura y automática. Esta integración vincula las transacciones de Fluxi directamente con tu cuenta."}
                    </p>

                    <div className="space-y-3 mb-6">
                        <div className="flex items-start gap-3">
                            <Lock className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["mercadopago-feature-secure"]?.[language] || "Procesamiento de pagos seguro"}
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["mercadopago-feature-sync"]?.[language] || "Conciliación automática de transacciones"}
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            <DollarSign className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                            <span className="text-sm text-gray-700">
                                {dictionary["mercadopago-feature-finance"]?.[language] || "Gestión financiera centralizada"}
                            </span>
                        </div>
                    </div>

                    <div className="mb-5">
                        <a
                            href="https://entrenamiento.fluxi.co/c/mercado-pago-cbb41e/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 p-3 pr-5 rounded-lg bg-[#f0f4ff] group cursor-pointer w-auto"
                        >
                            <div className="w-10 h-10 rounded-full bg-[#5050ea] flex items-center justify-center shrink-0 group-hover:bg-[#3b3bc4] transition-colors shadow-sm">
                                <PlayCircle className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[13px] font-semibold text-gray-800">{dictionary["mercadopago-video-tutorial"]?.[language] || "¿No sabes cómo conectar Mercado Pago?"}</span>
                                <span className="text-xs text-[#5050ea] font-medium">{dictionary["watch-video-tutorial"]?.[language] || "Ver video tutorial"}</span>
                            </div>
                        </a>
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {dictionary["public-token"][language]}
                                </label>
                                <input
                                    type="text"
                                    value={tokenPublic}
                                    onChange={(e) => setTokenPublic(e.target.value)}
                                    placeholder={dictionary["enter-your-public-token"][language]}
                                    className={`w-full p-2.5 rounded-lg border ${publicTokenError || invalidPublicFormat ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-100 focus:border-blue-500'} focus:outline-none focus:ring-2 transition-all text-sm`}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {dictionary["private-token"][language]}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={tokenPrivate}
                                        onChange={(e) => setTokenPrivate(e.target.value)}
                                        placeholder={dictionary["enter-your-private-token"][language]}
                                        className={`w-full p-2.5 pr-10 rounded-lg border ${privateTokenError || invalidPrivateFormat ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-100 focus:border-blue-500'} focus:outline-none focus:ring-2 transition-all text-sm`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {hasTestToken ? (
                                    <p className="text-red-500 text-xs mt-1">
                                        {dictionary["test-tokens-not-allowed-in-production"][language]}
                                    </p>
                                ) : hasInvalidFormat ? (
                                    <p className="text-red-500 text-xs mt-1">
                                        {dictionary["invalid-mercadopago-token-format"][language]}
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        {dictionary["mercadopago-credentials-hint"]?.[language] || "Credenciales en el Panel de Desarrollador de Mercado Pago."}
                                    </p>
                                )}
                            </div>

                            <div className="flex gap-3 mt-2">
                                <button
                                    type="submit"
                                    disabled={tokenPublic === "" || tokenPrivate === "" || loading || hasTestToken || hasInvalidFormat}
                                    className="flex-1 bg-[#009ee3] hover:bg-[#008bc7] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {dictionary["connecting"]?.[language] || "Conectando..."}
                                        </>
                                    ) : (
                                        dictionary["connect"][language]
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModalOpenMercadoPago(false)}
                                    className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
                                >
                                    {dictionary["cancel"][language]}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Right Side - Visual */}
                <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#eff6ff] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
                    <button
                        onClick={() => setModalOpenMercadoPago(false)}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
                    >
                        <X size={18} />
                    </button>

                    <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
                        <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(59,130,246,0.15)] border border-blue-100 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
                            <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
                        </div>

                        <div className="flex flex-col items-center gap-1 text-blue-300">
                            <ChevronDown strokeWidth={3} size={24} className="animate-[mpFade_1.5s_infinite_0ms]" />
                            <ChevronDown strokeWidth={3} size={24} className="animate-[mpFade_1.5s_infinite_200ms]" />
                            <ChevronDown strokeWidth={3} size={24} className="animate-[mpFade_1.5s_infinite_400ms]" />
                        </div>

                        <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
                            <img src={MERCADO_PAGO_LOGO} alt="Mercado Pago" className="w-12 h-12 object-contain" />
                        </div>

                        <style>{`
                            @keyframes mpFade {
                                0%, 100% { opacity: 0.3; transform: translateY(0); }
                                50% { opacity: 1; transform: translateY(3px); }
                            }
                        `}</style>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalMercadoPagoIntegration;
